import type { GeschaeftspartnerTyp, Partnerart } from "@/lib/kontakte";

export interface PartnerNameForm {
  partnerart: Partnerart;
  firmenname: string;
  anrede: string;
  vorname: string;
  nachname: string;
}

export function emptyPartnerName(): PartnerNameForm {
  return {
    partnerart: "firma",
    firmenname: "",
    anrede: "",
    vorname: "",
    nachname: "",
  };
}

export function splitPersonName(full: string): { vorname: string; nachname: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  const first = parts[0] ?? "";
  if (!first) return { vorname: "", nachname: "" };
  if (parts.length === 1) return { vorname: "", nachname: first };
  return { vorname: first, nachname: parts.slice(1).join(" ") };
}

export function partnerNameFromRecord(record: {
  partnerart?: Partnerart | null;
  firmenname?: string | null;
  anrede?: string | null;
  vorname?: string | null;
  nachname?: string | null;
}): PartnerNameForm {
  const partnerart: Partnerart = record.partnerart === "privatperson" ? "privatperson" : "firma";
  let vorname = record.vorname?.trim() || "";
  let nachname = record.nachname?.trim() || "";
  if (partnerart === "privatperson" && !vorname && !nachname) {
    const split = splitPersonName(record.firmenname || "");
    vorname = split.vorname;
    nachname = split.nachname;
  }
  return {
    partnerart,
    firmenname: record.firmenname || "",
    anrede: record.anrede?.trim() || "",
    vorname,
    nachname,
  };
}

export function switchPartnerart(form: PartnerNameForm, next: Partnerart): PartnerNameForm {
  if (next === form.partnerart) return form;
  if (next === "privatperson") {
    const hasName = form.vorname.trim() || form.nachname.trim();
    const split = hasName
      ? { vorname: form.vorname, nachname: form.nachname }
      : splitPersonName(form.firmenname);
    return {
      ...form,
      partnerart: "privatperson",
      vorname: split.vorname,
      nachname: split.nachname,
    };
  }
  const firmenname =
    form.firmenname.trim() || [form.vorname, form.nachname].filter((part) => part.trim()).join(" ");
  return { ...form, partnerart: "firma", firmenname };
}

export function validatePartnerName(
  form: PartnerNameForm,
  typ: GeschaeftspartnerTyp,
): string | null {
  if (typ === "kunde" && form.partnerart === "privatperson") {
    if (!form.vorname.trim() || !form.nachname.trim()) {
      return "Bitte Vorname und Nachname angeben.";
    }
    return null;
  }
  if (!form.firmenname.trim()) {
    return "Bitte einen Firmennamen eingeben.";
  }
  return null;
}

export function partnerNamePayload(form: PartnerNameForm, typ: GeschaeftspartnerTyp) {
  if (typ === "kunde" && form.partnerart === "privatperson") {
    const vorname = form.vorname.trim();
    const nachname = form.nachname.trim();
    return {
      partnerart: "privatperson" as const,
      firmenname: [vorname, nachname].filter(Boolean).join(" "),
      anrede: form.anrede.trim() || null,
      vorname,
      nachname,
    };
  }
  return {
    partnerart: "firma" as const,
    firmenname: form.firmenname.trim(),
    anrede: null,
    vorname: null,
    nachname: null,
  };
}
